/**
 * VitalSleep Product Section — JavaScript
 * File: assets/vitalsleep-product.js
 *
 * Handles: Swiper slider, video overlay, variant selection,
 *          add to cart, accordion expand/collapse, modal open/close.
 *
 * Wrapped in an IIFE so nothing leaks to the global scope.
 * Uses data-section IDs so multiple instances on the same
 * page (e.g. Quick Buy) won't collide.
 *
 * 25-Jul-2026 refactor notes:
 *  - Add to cart no longer depends on the hardcoded #btnCTA id,
 *    and no longer sits behind the Swiper guard or a nested
 *    DOMContentLoaded listener that could never fire.
 *  - Video + play overlay are now resolved per slide by class,
 *    so duplicate ids no longer matter and N videos are supported.
 *  - Changing size no longer destroys the CTA's two-line markup
 *    (guarantee sub-line) or the price box's discount badge.
 */
(function () {
  'use strict';

  /* ── Small helpers ───────────────────────────────────────── */
  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function closestBySelector(el, selector) {
    if (el && el.closest) return el.closest(selector);
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(selector)) return node;
      node = node.parentNode;
    }
    return null;
  }

  /* ────────────────────────────────────────────────────────
     ADD TO CART
     Bound separately from initSection so a missing or slow
     Swiper can never stop the buy button from working.
     Scoped by .vs-cta-btn inside the section root, so the
     id="btnCTA" attribute is left in place for any app that
     targets it without this file depending on it.
  ──────────────────────────────────────────────────────── */
  function initAddToCart(sectionId) {
    var root = document.getElementById('vs-pdp-' + sectionId);
    if (!root) return;

    var button = root.querySelector('.vs-cta-btn');
    var variantInput = document.getElementById('vs-variant-id-' + sectionId);
    if (!button || button.dataset.vsCartBound === '1') return;
    button.dataset.vsCartBound = '1';

    button.addEventListener('click', function () {
      if (button.disabled || button.classList.contains('loading')) return;

      var selectedVariant = variantInput ? variantInput.value : '';
      if (!selectedVariant) {
        alert('Please select a size.');
        return;
      }

      button.classList.add('loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedVariant, quantity: 1 })
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          var goToCart = (typeof window.redirectToCart !== 'undefined') && window.redirectToCart;
          if (goToCart) {
            window.location.href = '/cart';
            return;
          }
          if (typeof window.SLIDECART_UPDATE === 'function') window.SLIDECART_UPDATE();
          if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
        })
        .catch(function (err) { console.error(err); })
        .then(function () {
          button.classList.remove('loading');
        });
    });
  }

  /* ────────────────────────────────────────────────────────
     VARIANT / SIZE SELECTOR
     Also bound outside the Swiper guard so size selection
     keeps working if the gallery fails to initialise.
  ──────────────────────────────────────────────────────── */
  function initVariants(sectionId) {
    var root = document.getElementById('vs-pdp-' + sectionId);
    if (!root) return;

    var variantInput = document.getElementById('vs-variant-id-' + sectionId);
    var sizeSection = document.getElementById('vs-size-section-' + sectionId);
    if (!sizeSection || sizeSection.dataset.vsVariantsBound === '1') return;
    sizeSection.dataset.vsVariantsBound = '1';

    /* Build a lookup map: joined option values → variant object.
       window.__vs_variants is populated by the vitalsleep-variants.liquid snippet. */
    var variantMap = {};
    var variantList = (window.__vs_variants && window.__vs_variants[sectionId]) || [];
    variantList.forEach(function (v) {
      variantMap[v.options.join('||')] = v;
    });

    function buildComboKey() {
      var radios = sizeSection.querySelectorAll('.vs-size-radio:checked');
      var sorted = toArray(radios).sort(function (a, b) {
        return parseInt(a.getAttribute('data-option-index'), 10) -
               parseInt(b.getAttribute('data-option-index'), 10);
      });
      return sorted.map(function (r) { return r.value; }).join('||');
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try {
          var fmt = (window.Shopify.money_format || window.theme && window.theme.moneyFormat);
          var out = fmt ? window.Shopify.formatMoney(cents, fmt) : window.Shopify.formatMoney(cents);
          if (out) return out;
        } catch (e) { /* fall through */ }
      }
      return '$' + (cents / 100).toFixed(2);
    }

    /* Update the price without destroying the (x% OFF) badge */
    function updatePrice(variant) {
      var priceEl = root.querySelector('.vs-price-main');
      if (!priceEl || typeof variant.price !== 'number') return;

      var saveEl = priceEl.querySelector('.vs-save-text');
      var compare = variant.compare_at_price;

      priceEl.textContent = formatMoney(variant.price);

      if (saveEl) {
        if (typeof compare === 'number') {
          if (compare > variant.price) {
            var pct = Math.round(((compare - variant.price) / compare) * 100);
            saveEl.textContent = '(' + pct + '% OFF)';
            priceEl.appendChild(document.createTextNode(' '));
            priceEl.appendChild(saveEl);
          }
          /* compare <= price: no discount on this variant, badge stays off */
        } else {
          /* No compare data available — keep the badge as rendered by Liquid */
          priceEl.appendChild(document.createTextNode(' '));
          priceEl.appendChild(saveEl);
        }
      }

      /* 16-Sep-2026: Shop Pay line amount follows the selected variant (price / 4, rounded up). */
      toArray(root.querySelectorAll('.vs-installment')).forEach(function (el) {
        var n = parseInt(el.getAttribute('data-vs-installments'), 10) || 4;
        el.textContent = String(formatMoney(Math.ceil(variant.price / n))).replace(/<[^>]*>/g, '').replace(/\s+/g, '');
      });

      var listEl = root.querySelector('.vs-list-price');
      if (listEl && typeof compare === 'number' && compare > variant.price) {
        listEl.textContent = formatMoney(compare);
      }
    }

    /* Update the CTA in place. Never rebuild innerHTML — that wiped
       out .cta-two-line, .cta-text and the .cta-subtext guarantee line. */
    function updateCta(variant) {
      var ctaBtn = root.querySelector('.vs-cta-btn');
      if (!ctaBtn) return;

      var available = !!variant.available;
      ctaBtn.disabled = !available;

      var textEl = ctaBtn.querySelector('.cta-text');
      var subEl = ctaBtn.querySelector('.cta-subtext');
      var label = ctaBtn.getAttribute('data-cta-text') || 'Start Sleeping Quieter →';

      if (textEl) textEl.textContent = available ? label : 'Sold Out';
      if (subEl) subEl.style.display = available ? '' : 'none';
    }

    function applyVariant(variant) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      updatePrice(variant);
      updateCta(variant);

      if (window.history && window.history.replaceState && variant.id) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* older browsers — skip */ }
      }
    }

    function onRadioChange(radio) {
      var idx = parseInt(radio.getAttribute('data-option-index'), 10);
      var value = radio.value;
      var labelId = radio.getAttribute('data-label-id');

      var labelEl = document.getElementById(labelId);
      if (labelEl) labelEl.textContent = value;

      toArray(sizeSection.querySelectorAll('.vs-size-btn[data-option-index="' + idx + '"]'))
        .forEach(function (lbl) { lbl.classList.remove('vs-active'); });

      var parentLabel = closestBySelector(radio, '.vs-size-btn');
      if (parentLabel) parentLabel.classList.add('vs-active');

      applyVariant(variantMap[buildComboKey()]);
    }

    toArray(sizeSection.querySelectorAll('.vs-size-radio')).forEach(function (radio) {
      radio.addEventListener('change', function () { onRadioChange(radio); });
    });

    /* On first load: derive the variant from the pre-checked radios */
    var initialVariant = variantMap[buildComboKey()];
    if (initialVariant && variantInput) {
      variantInput.value = initialVariant.id;
    }
  }

  /* ────────────────────────────────────────────────────────
     GALLERY — Swiper, thumbnails, videos
  ──────────────────────────────────────────────────────── */
  function initGallery(sectionId) {
    var root = document.getElementById('vs-pdp-' + sectionId);
    if (!root) return;

    var thumbEl = document.getElementById('vs-thumb-swiper-' + sectionId);
    var mainEl = document.getElementById('vs-main-swiper-' + sectionId);
    if (typeof Swiper === 'undefined' || !thumbEl || !mainEl) return;
    if (mainEl.dataset.vsGalleryBound === '1') return;
    mainEl.dataset.vsGalleryBound = '1';

    var thumbSwiper = new Swiper(thumbEl, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchSlidesProgress: true,
      slideToClickedSlide: true
    });

    var mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 400,
      thumbs: { swiper: thumbSwiper },
      on: {
        slideChange: function () {
          /* Pause every video that is not on the active slide.
             Previously this assumed the video was always slide index 1,
             which broke whenever the gallery was reordered. */
          var activeSlide = this.slides ? this.slides[this.activeIndex] : null;
          toArray(mainEl.querySelectorAll('video')).forEach(function (vid) {
            var slide = closestBySelector(vid, '.swiper-slide');
            if (slide !== activeSlide && !vid.paused) vid.pause();
          });
        }
      }
    });

    thumbSwiper.on('click', function () {
      if (thumbSwiper.clickedIndex !== undefined) {
        mainSwiper.slideTo(thumbSwiper.clickedIndex);
      }
    });

    /* Prev / Next thumb nav buttons */
    var prevBtn = document.getElementById('vs-thumb-prev-' + sectionId);
    var nextBtn = document.getElementById('vs-thumb-next-' + sectionId);

    function syncNav() {
      if (prevBtn) prevBtn.disabled = thumbSwiper.isBeginning;
      if (nextBtn) nextBtn.disabled = thumbSwiper.isEnd;
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { thumbSwiper.slidePrev(); syncNav(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { thumbSwiper.slideNext(); syncNav(); });
    thumbSwiper.on('slideChange', syncNav);
    syncNav();

    /* ── Video play overlays ──────────────────────────────
       Resolved per slide by class rather than by id, so the
       duplicate id="vs-product-video-{section.id}" emitted
       inside the media loop no longer matters and any number
       of videos works correctly.
    ─────────────────────────────────────────────────────── */
    toArray(mainEl.querySelectorAll('.vs-video-slide')).forEach(function (slide) {
      var video = slide.querySelector('video');
      var overlay = slide.querySelector('.vs-play-overlay');
      if (!overlay) return;

      if (video) {
        overlay.addEventListener('click', function () {
          var playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        });
        video.addEventListener('play', function () {
          overlay.classList.add('vs-hidden');
        }, { once: true });
      } else {
        /* External video (YouTube / Vimeo iframe) — just dismiss the overlay */
        overlay.addEventListener('click', function () {
          overlay.classList.add('vs-hidden');
        });
      }
    });
  }

  /* ────────────────────────────────────────────────────────
     ACCORDION — exact scrollHeight transitions
  ──────────────────────────────────────────────────────── */
  function initAccordions(sectionId) {
    function openPanel(trigger, panel) {
      var inner = panel.querySelector('.vs-accordion-panel-inner');
      trigger.setAttribute('aria-expanded', 'true');
      panel.style.height = (inner ? inner.scrollHeight : panel.scrollHeight) + 'px';
      function onEnd() { panel.style.height = 'auto'; panel.removeEventListener('transitionend', onEnd); }
      panel.addEventListener('transitionend', onEnd);
    }

    function closePanel(trigger, panel) {
      if (!panel) return;
      panel.style.height = panel.scrollHeight + 'px';
      panel.offsetHeight; /* force reflow */
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.height = '0';
    }

    /* Accu-Adjust: starts closed */
    var accuTrigger = document.getElementById('vs-accu-trigger-' + sectionId);
    var accuPanel = document.getElementById('vs-accu-panel-' + sectionId);
    if (accuTrigger && accuPanel && accuTrigger.dataset.vsBound !== '1') {
      accuTrigger.dataset.vsBound = '1';
      accuTrigger.addEventListener('click', function () {
        if (accuTrigger.getAttribute('aria-expanded') === 'true') {
          closePanel(accuTrigger, accuPanel);
        } else {
          openPanel(accuTrigger, accuPanel);
        }
      });
    }

    /* FAQ items: only one open at a time within the section */
    var faqList = document.getElementById('vs-faq-list-' + sectionId);
    if (!faqList || faqList.dataset.vsBound === '1') return;
    faqList.dataset.vsBound = '1';

    var faqTriggers = toArray(faqList.querySelectorAll('.vs-faq-trigger'));

    faqTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var panelId = trigger.getAttribute('aria-controls');
        var panel = document.getElementById(panelId);
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';

        faqTriggers.forEach(function (t) {
          var p = document.getElementById(t.getAttribute('aria-controls'));
          if (t.getAttribute('aria-expanded') === 'true') closePanel(t, p);
        });

        if (!isOpen && panel) openPanel(trigger, panel);
      });
    });
  }

  /* ────────────────────────────────────────────────────────
     MODAL — open / close / backdrop click / Escape key
  ──────────────────────────────────────────────────────── */
  function vsOpenModal(modalId) {
    var backdrop = document.getElementById(modalId);
    if (!backdrop) return;
    backdrop.style.display = 'flex';
    backdrop.offsetHeight; /* force reflow so transition fires */
    backdrop.classList.add('vs-modal-open');
    document.body.style.overflow = 'hidden';
    backdrop.dataset.active = '1';
    var card = backdrop.querySelector('.vs-modal-card');
    if (card) { card.setAttribute('tabindex', '-1'); card.focus(); }
  }

  function vsCloseModal(modalId) {
    var backdrop = document.getElementById(modalId);
    if (!backdrop) return;
    toArray(backdrop.querySelectorAll('video')).forEach(function (video) {
      video.pause();
    });
    backdrop.classList.remove('vs-modal-open');
    backdrop.addEventListener('transitionend', function hide() {
      backdrop.style.display = 'none';
      backdrop.removeEventListener('transitionend', hide);
    });
    delete backdrop.dataset.active;
    if (!document.querySelectorAll('.vs-modal-backdrop[data-active]').length) {
      document.body.style.overflow = '';
    }
  }

  function initModals(sectionId) {
    var root = document.getElementById('vs-pdp-' + sectionId);
    if (!root) return;

    /* Open triggers inside this section root */
    toArray(root.querySelectorAll('[data-modal]')).forEach(function (btn) {
      if (btn.dataset.vsBound === '1') return;
      btn.dataset.vsBound = '1';
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        vsOpenModal(btn.getAttribute('data-modal'));
      });
    });

    /* Close buttons live outside root, inside the backdrop */
    toArray(document.querySelectorAll('[data-close-modal]')).forEach(function (btn) {
      if (btn.dataset.vsBound === '1') return;
      btn.dataset.vsBound = '1';
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        vsCloseModal(btn.getAttribute('data-close-modal'));
      });
    });

    /* Backdrop click-outside → close */
    toArray(document.querySelectorAll('.vs-modal-backdrop')).forEach(function (bd) {
      if (bd.dataset.vsBound === '1') return;
      bd.dataset.vsBound = '1';
      bd.addEventListener('click', function (e) {
        if (e.target === bd) vsCloseModal(bd.id);
      });
    });
  }

  /* Escape key — registered once for the whole page */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      toArray(document.querySelectorAll('.vs-modal-backdrop[data-active]')).forEach(function (bd) {
        vsCloseModal(bd.id);
      });
    }
  });

  /* ────────────────────────────────────────────────────────
     REVIEWS — star bar and #reviews link scroll to the widget
  ──────────────────────────────────────────────────────── */
  function initReviewScroll(sectionId) {
    var root = document.getElementById('vs-pdp-' + sectionId);
    if (!root) return;

    var ratingBlock = root.querySelector('.vs-stars');
    if (ratingBlock && ratingBlock.dataset.vsBound !== '1') {
      ratingBlock.dataset.vsBound = '1';
      ratingBlock.addEventListener('click', function () {
        var reviewsWidget = document.querySelector('.okeReviews-widget-holder');
        if (reviewsWidget) reviewsWidget.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }

  function initReviewLinks() {
    toArray(document.querySelectorAll('a[href="#reviews"]')).forEach(function (link) {
      if (link.dataset.vsBound === '1') return;
      link.dataset.vsBound = '1';
      link.addEventListener('click', function (e) {
        var reviewsSection = document.querySelector('.okeReviews-widget-holder');
        if (!reviewsSection) return;
        e.preventDefault();
        reviewsSection.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  /* ────────────────────────────────────────────────────────
     BOOTSTRAP
     Commerce first (cart, variants, accordions, modals) so a
     missing or slow Swiper can never block the buy button.
     The gallery is then retried until Swiper is available.
  ──────────────────────────────────────────────────────── */
  function initSection(sectionId) {
    initAddToCart(sectionId);
    initVariants(sectionId);
    initAccordions(sectionId);
    initModals(sectionId);
    initReviewScroll(sectionId);
    initGallery(sectionId);
  }

  function sectionIds() {
    return toArray(document.querySelectorAll('[id^="vs-pdp-"]')).map(function (el) {
      return el.id.replace('vs-pdp-', '');
    });
  }

  function bootCommerce() {
    sectionIds().forEach(function (id) {
      initAddToCart(id);
      initVariants(id);
      initAccordions(id);
      initModals(id);
      initReviewScroll(id);
    });
    initReviewLinks();
  }

  function bootGallery(attempts) {
    if (typeof Swiper !== 'undefined') {
      sectionIds().forEach(initGallery);
    } else if (attempts > 0) {
      setTimeout(function () { bootGallery(attempts - 1); }, 100);
    }
  }

  function boot() {
    bootCommerce();
    bootGallery(20);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ── Shopify Theme Editor live reloads (section refresh) ── */
  document.addEventListener('shopify:section:load', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id) setTimeout(function () { initSection(id); }, 50);
  });

})();
